import type { MetaNodeDef } from '../../game/meta';
import { Bomb, BOMB_SPECS } from '../../sim/bombs';

/* ═══════════════════════════════════════════════════════════════════════════
   LES TROIS BOMBES — le catalogue.

   Une seule s'emporte par run (§6.3). Ces définitions ne portent AUCUN chiffre
   de jeu : les durées, les rayons et les dégâts vivent dans
   `src/sim/bombs.ts`, parce qu'ils sont des capacités de moteur et non des
   réglages de contenu — une colonne de dégâts dans le temps et une dilatation
   du temps des projectiles ne sont pas deux instances d'une même forme
   paramétrable.

   Ce qu'on trouve ici : l'identifiant que l'arbre accorde, le nom, et le texte
   affiché au briefing. Le moteur, lui, ne connaît qu'un entier.

   ⚠️ ÉCLAT n'est accordée par aucun nœud. Elle est OFFERTE, comme la Lance
   perforante côté secondaires : la touche bombe doit faire quelque chose dès la
   première partie, pour un joueur qui n'a rien acheté et n'a jamais vu d'écran
   de choix. Le contrôle de complétude plus bas l'exclut donc explicitement.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface BombDef {
  /** Identifiant EXACT du `grants` du nœud, sauf pour la bombe offerte. */
  readonly id: string;
  readonly name: string;
  /** Texte du briefing. Chiffré, pour que les trois se comparent d'un coup
   *  d'œil — c'est un CHOIX, pas une montée en puissance, et un choix ne se
   *  fait pas sur des adjectifs. */
  readonly text: string;
  /** Variante de moteur (`Bomb.*`), à passer à `World.armPlayer`. */
  readonly kind: number;
}

/** La bombe offerte, celle d'un joueur à méta zéro. */
export const DEFAULT_BOMB_ID = 'bombe-eclat';

export const BOMBS: readonly BombDef[] = [
  {
    id: DEFAULT_BOMB_ID,
    name: 'ÉCLAT',
    text: '1,60 s d’invulnérabilité. Aucun effet de bord.',
    kind: Bomb.Eclat,
  },
  {
    id: 'bombe-brisant',
    name: 'BRISANT',
    text: '0,90 s d’invulnérabilité, colonne de 260 u, 3 000 dégâts sur 2,2 s.',
    kind: Bomb.Brisant,
  },
  {
    id: 'bombe-temps-mort',
    name: 'TEMPS MORT',
    text: '1,20 s d’invulnérabilité, balles à 25 % de vitesse pendant 3,5 s, +50 % de valeur de frôlement.',
    kind: Bomb.TempsMort,
  },
];

/** Préfixe de famille, fixé par l'en-tête de `tree.def.ts`. */
const BOMB_PREFIX = 'bombe-';

export function bombByGrant(id: string): BombDef | undefined {
  return BOMBS.find((b) => b.id === id);
}

/** Bombes emportables. ÉCLAT est TOUJOURS présente, même si aucun nœud ne
 *  l'accorde — voir l'en-tête. */
export function unlockedBombs(
  nodes: readonly MetaNodeDef[],
  bought: ReadonlySet<string>,
): BombDef[] {
  const accordes = new Set<string>([DEFAULT_BOMB_ID]);
  for (const n of nodes) {
    if (n.kind !== 'option' || !bought.has(n.id)) continue;
    accordes.add(n.grants);
  }
  return BOMBS.filter((b) => accordes.has(b.id));
}

/**
 * Variante de moteur à armer, en retombant sur ÉCLAT si le choix mémorisé n'est
 * plus valide.
 *
 * ⚠️ Le repli n'est pas de la paranoïa, c'est le même défaut que celui corrigé
 * pour les armes secondaires : une sauvegarde peut désigner une bombe retirée
 * du catalogue. Sans repli, la partie démarrerait avec une variante inconnue —
 * donc, après écrêtage, ÉCLAT quand même, mais par accident et sans que le
 * briefing ait affiché la bonne. On préfère un repli explicite à un écrêtage
 * silencieux.
 */
export function resolveBomb(choisie: string, disponibles: readonly BombDef[]): number {
  const trouvee = disponibles.find((b) => b.id === choisie);
  if (trouvee) return trouvee.kind;
  return disponibles[0]?.kind ?? Bomb.Eclat;
}

/** Bombes que l'arbre VEND et que le moteur n'implémente pas — un nœud à
 *  500 crédits qui s'affiche « ACQUIS » et ne change rien à la touche. */
export function unimplementedBombs(nodes: readonly MetaNodeDef[]): string[] {
  const connues = new Set(BOMBS.map((b) => b.id));
  const out: string[] = [];
  for (const n of nodes) {
    if (n.kind !== 'option') continue;
    if (!n.grants.startsWith(BOMB_PREFIX)) continue;
    if (!connues.has(n.grants)) out.push(n.grants);
  }
  return out;
}

/** Bombes implémentées qu'aucun nœud n'ouvre. ÉCLAT est exclue : elle est
 *  offerte, donc n'a pas à être accordée. */
export function unreachableBombs(nodes: readonly MetaNodeDef[]): string[] {
  const accordes = new Set<string>([DEFAULT_BOMB_ID]);
  for (const n of nodes) if (n.kind === 'option') accordes.add(n.grants);
  return BOMBS.filter((b) => !accordes.has(b.id)).map((b) => b.id);
}

/** Contrôle de cohérence entre le texte affiché et les chiffres du moteur.
 *
 *  ⚠️ Le seul écart qu'aucun test de comportement ne rattrape : une bombe qui
 *  fonctionne parfaitement et dont le briefing annonce autre chose. Le joueur
 *  paie sur la foi du texte ; c'est donc le texte qui doit être vérifié, pas
 *  seulement l'effet. On se limite à l'invulnérabilité, la seule grandeur que
 *  les trois textes annoncent tous. */
export function bombTextMismatches(): string[] {
  const out: string[] = [];
  for (const b of BOMBS) {
    const spec = BOMB_SPECS[b.kind];
    if (!spec) {
      out.push(`${b.id} : variante ${b.kind} inconnue du moteur`);
      continue;
    }
    // « 0,90 s » — on relit le nombre tel qu'il est écrit, virgule comprise.
    const annonce = `${spec.iframes.toFixed(2).replace('.', ',')} s d’invulnérabilité`;
    if (!b.text.includes(annonce)) {
      out.push(`${b.id} : le texte n'annonce pas « ${annonce} »`);
    }
  }
  return out;
}
